import React, { useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Constants, USER_ROLES } from '@pwc/pwc-deals-web-common';

import { getUserRole } from 'common/redux/security/loggedInUser/loggedInUserSlice';

import {
  shape,
  bool,
  number,
  string,
  oneOf,
  func,
  oneOfType,
} from 'prop-types';
import { Avatar, Button, Icon } from '@pwc/pwc-deals-ui';
import classNames from 'classnames';
import { getAnalysisDescription } from 'common/engine/reducers/structuredDocumentDescriptionSlice';
import RibbonPanelIcon from '../RibbonPanel/RibbonPanelIcon.tsx';
import ShowForRole from '../../../features/security/ShowForRole';
import VersionToggleButtons from './VersionToggleButtons';
import { COMMENT_VIEWING_STATUS } from '../../../features/comments/constants';
import {
  getCommentPanelOpened,
  getViewingStatus,
  viewUnresolvedComments,
  setCommentPanelOpened,
} from '../../../features/comments/commentsSlice';
import './VersionToggleContainer.scss';
import { isReportLivePreview } from '../../helpers/featureFlags';
import useSmallMobile from '../../hooks/useSmallMobile';
import useLock from '../../hooks/useLock';

const VersionToggleContainer = ({
  projectId: projectIdInput,
  config = {},
  isEntitySharedWithClient = false,
  mode = 'view',
  lockedThreadId,
  disableVersionToggleButtons = false,
  hideTextVersion = false,
  showSelected = true,
  ...rest
}) => {
  const projectId = projectIdInput.toString();
  const dispatch = useDispatch();
  const commentPanelOpened = useSelector(getCommentPanelOpened);
  const viewingStatus = useSelector(getViewingStatus);
  const isPwcUser = useSelector(getUserRole) === USER_ROLES.PWC_USER;
  const { loading } = useSelector(getAnalysisDescription);
  const isSmallMobile = useSmallMobile();

  const { name, isLocked } = useLock({ contentThreadId: lockedThreadId });

  const commentIconName = useMemo(() => {
    if (isReportLivePreview) {
      return commentPanelOpened ? 'close' : 'ribbon-comment';
    }
    return commentPanelOpened ? 'right-chevron' : 'comment-chevron-left';
  }, [commentPanelOpened]);

  const commentIconWidth = commentPanelOpened ? 15 : 30;

  const handlePanelButtonClick = () => {
    if (viewingStatus !== COMMENT_VIEWING_STATUS.UNRESOLVED) {
      dispatch(viewUnresolvedComments());
    }
    dispatch(setCommentPanelOpened(!commentPanelOpened));
  };

  return (
    <div
      className={classNames('version-toggle-container', {
        'current-user-is-client': !isPwcUser,
      })}
    >
      <ShowForRole
        projectId={projectId}
        roles={Constants.USER_ROLE_GROUPS.INTERNAL}
      >
        <VersionToggleButtons
          config={config}
          disabled={disableVersionToggleButtons || loading}
          hideTextVersion={hideTextVersion}
          isEntitySharedWithClient={isEntitySharedWithClient}
          mode={mode}
          showSelected={showSelected}
          {...rest}
        />
      </ShowForRole>
      {isLocked && isSmallMobile && <Avatar name={name} />}
      <div className="comment-panel-button">
        <Button
          className="panel-button"
          aria-label="comment icon"
          onClick={handlePanelButtonClick}
          data-testid="panel-button"
        >
          {commentIconName === 'ribbon-comment' ? (
            <RibbonPanelIcon name="comment" />
          ) : (
            <Icon
              name={commentIconName}
              className="fill icon-orange"
              width={commentIconWidth}
            />
          )}
        </Button>
      </div>
    </div>
  );
};

VersionToggleContainer.propTypes = {
  projectId: oneOfType([string, number]),
  config: shape({
    draftVersion: shape({
      versionNumber: number,
    }),
    sharedVersion: shape({
      versionNumber: number,
    }),
    commentCounts: shape({
      draftVersion: shape({
        commentCount: number,
      }),
      sharedVersion: shape({
        commentCount: number,
      }),
    }),
    draftOnClick: func,
    sharedOnClick: func,
  }),
  isEntitySharedWithClient: bool,
  mode: oneOf(['view', 'edit']),
};

VersionToggleContainer.defaultProps = {
  projectId: '0',
  config: {},
  isEntitySharedWithClient: false,
  mode: 'view',
};

export default VersionToggleContainer;
